import type { DocNode } from '@atlaskit/adf-schema';
import { type IconProps } from '@atlaskit/icon/types';
import type { UploadedFile } from '@atlassian/conversation-assistant-chat-prompt-input/UploadedFile';
import type {
	Agent,
	Dialogues,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { JtbdCategory } from '@atlassian/convo-ai-api';
import type { Skill } from '@atlassian/rovo-platform-ui-components/skills/types';

//TODO: Determine if we will have our own new way of handling ViewConfigs (may be able to break away from current Rovo Button ViewConfig typing)
export type ViewConfig = {
	render: () => React.ReactElement;
};

export type AIActionType = 'ai-action' | 'agent-action' | 'convo-starter-action' | 'skill-action';

export enum HandleActionType {
	Base = 'Base',
	Chat = 'Chat',
	Nested = 'Nested',
	Skill = 'Skill',
}

export type CustomFollowUpContext = {
	available: boolean;
	fetch: () => Promise<Dialogues[]>;
};

/*
 * Read more about the BaseConversationAction type at `go/rovo-button`
 */
export type BaseConversationAction = {
	id: string;
	title: string;
	secondaryTitle?: string;
	/**
	 * Optional fully-custom content renderer. When set, replaces the standard
	 * icon + title + hover-description layout inside the nudge pill. The outer
	 * pill container, dismiss button, animation, and analytics are all preserved.
	 * Use when a nudge needs a bespoke layout (e.g. always-visible description,
	 * different icon placement, multi-line content).
	 */
	customContent?: () => React.ReactNode;
	description?: string;
	secondaryDescription?: string;
	//TODO: Remove React.ReactElement when RovoButton AI Action is updated to match iconography needs
	icon: (props: IconProps) => React.JSX.Element | React.ReactElement;
	secondaryIcon?: (props: IconProps) => React.JSX.Element | React.ReactElement;
	descriptionIcon?: (props: IconProps) => React.JSX.Element;
	onClick?: () => void;
	viewConfig?: ViewConfig;
	children?: Array<BaseConversationAction>;
	href?: string;
	closePanelOnSelection?: boolean;
	ref?: React.Ref<HTMLButtonElement>;
	type?: AIActionType;
	handleType: HandleActionType;
	isChild?: boolean;
	customFollowUpContext?: CustomFollowUpContext;
	nudgeContext?: {
		id: string;
		experimentName?: string;
		/** When true, the consumer has determined eligibility — nudge is shown without BE ranking. */
		selfEligible?: boolean;
		/** Scopes suppression to a specific context (e.g. a conversation ID or content ID). When absent, suppression applies to the nudge type as a whole. */
		suppressionKey?: string;
		/** Called when the user clicks the nudge (opens chat). Use for persistent storage side-effects. Does NOT open chat — triggerAction handles that. */
		onAccept?: () => void;
		/** Called when the user explicitly dismisses the nudge (X button). Use for persistent storage side-effects. */
		onDismiss?: () => void;
		/**
		 * Optional extra analytics attributes that the consumer wants to forward
		 * onto the `aiRovoProactiveNudge` events.
		 */
		analyticsAttributes?: Record<string, string | number | boolean>;
	};
	badge?: React.ReactNode;
	/**
	 * When true, the action's secondary text (`secondaryDescription` for
	 * chat/base actions, `description` for skill actions) is always visible
	 * instead of being revealed on hover. Opt-in per action; defaults to the
	 * existing hover-reveal behaviour.
	 */
	alwaysShowSecondaryText?: boolean;
};

/*
 * Conversation action on click will start a conversation with the provided agent and prompt.
 * At least one of dynamicPrompt or staticPrompt must be provided.
 * Read more about the ChatAction type at `go/rovo-button`
 */
export type ChatAction = BaseConversationAction & {
	dynamicPrompt?: string;
	staticPrompt?: string;
	agent?: Agent;
	starterType?: 'static' | 'dynamic'; // self-defined categorization for static vs dynamic starters
	jtbdCategory?: JtbdCategory; // exclusive to dynamic starters, categorized by the Backend
	handleType: HandleActionType.Chat;
	uploadedFiles?: UploadedFile[];
	/**
	 * When true, the dynamicPrompt will be encoded as a skill command in the chat input,
	 * rendering any leading slash-command (e.g. `/create-work-items`) as a styled skill pill.
	 */
	encodeDynamicPromptAsSkill?: boolean;
	/**
	 * ADF document used to seed the chat input instead of the plain-string
	 * `dynamicPrompt`. When provided, rich nodes (skill pills, links, inline cards, mentions)
	 * are preserved rather than being flattened to text.
	 */
	dynamicPromptAdf?: DocNode;
};

/*
 * Mapping of JTBD categories to their string representations
 */
export type ConvoStarterCategoriesMap = {
	[key in JtbdCategory]: number;
};

/*
 * Nested action on click will open the action menu with the provided children for selection.
 * Read more about the NestedAction type at `go/rovo-button`
 */
export type NestedAction = BaseConversationAction & {
	children: Array<BaseConversationAction>;
	actionHeading?: string;
	handleType: HandleActionType.Nested;
};

/*
 * Skill action on click will start a conversation with the provided skill and prompt right away.
 * New skill actions must use `staticPrompt` (auto-send). Manual invocation via `dynamicPrompt`
 * is no longer supported — use `LegacyDynamicSkillAction` for grandfathered skills only.
 * Read more about the SkillAction type at `go/rovo-button`
 */
export type SkillAction = BaseConversationAction &
	Partial<Skill> & {
		type: Extract<AIActionType, 'skill-action'>;
		handleType: HandleActionType.Skill;
		staticPrompt: string;
	};

/**
 * @deprecated Use `SkillAction` with `staticPrompt` instead.
 * This type exists only to support existing adminhub skill actions that use `dynamicPrompt`.
 * Do not use this type for new skill actions — it will be removed in a future release.
 */
export type LegacyDynamicSkillAction = BaseConversationAction &
	Partial<Skill> & {
		type: Extract<AIActionType, 'skill-action'>;
		handleType: HandleActionType.Skill;
		dynamicPrompt: string;
		staticPrompt?: string;
	};

/**
 * Hardcoded allowlist of skill action IDs that are permitted to use `LegacyDynamicSkillAction`.
 * These are existing adminhub skill actions that were onboarded before manual invocation was removed.
 * Do not add new IDs to this list — new skill actions must use `SkillAction` with `staticPrompt`.
 */
export const LEGACY_DYNAMIC_SKILL_IDS: ReadonlySet<string> = new Set([
	'invite-users',
	'grant-app-access',
	'troubleshoot-invitation-errors',
	'troubleshoot-user-access',
	'troubleshoot-user-provisioning',
	'troubleshoot-single-sign-on',
	'review-api-security',
	'policy-assist',
]);

export type ConversationAction =
	| BaseConversationAction
	| ChatAction
	| NestedAction
	| SkillAction
	| LegacyDynamicSkillAction;

/**
 * Tags configuration for conversation actions.
 * Used to identify skill registry tags and other metadata.
 */
export type ConversationActionTags = {
	/**
	 * Tags identifying sets of skills registered in the backend `integrations-config-registry`.
	 * These tags are used to fetch skill metadata (description, icons, colors, etc.)
	 * and must match the tags associated with skills in
	 * https://bitbucket.org/atlassian/integrations-config-registry
	 */
	skillRegistryTags?: string[];
};

/**
 * Individual skill metadata from the backend.
 */
export type SkillMetadata = {
	name: string;
	description: string;
	iconName?: Skill['iconName'];
	product?: string;
	productIconUrl?: string;
	color?: Skill['color'];
};

/**
 * Map of skill slugs to their metadata.
 * The slug is extracted from the skill's slashCommand (e.g., "/jira-issues" -> "jira-issues").
 */
export type SkillMetadataMap = {
	[skillSlug: string]: SkillMetadata;
};

// conversation starter types
export type ConversationStarterWithLabel = ConversationStarter & {
	labelType: ConversationStarterLabelType;
};

type ConversationStarterTypeAIMate = 'static';

export type ConversationStarter = { message: string; type: ConversationStarterTypeAIMate };

export enum PageContextType {
	ADMIN_HUB = 'admin-hub',
	BITBUCKET = 'bitbucket',
	BITBUCKET_PULL_REQUEST = 'bitbucket-pull-request',
	BITBUCKET_CREATE_PULL_REQUEST = 'bitbucket-create-pull-request',
	BITBUCKET_PULL_REQUEST_WITHOUT_PIPELINES_STARTERS = 'bitbucket-pull-request-without-pipelines-starters',
	BITBUCKET_PIPELINES = 'bitbucket-pipelines',
	BITBUCKET_REPOSITORY_SOURCE = 'bitbucket-repository-source',
	CONFLUENCE_HOME = 'confluence-home',
	CONFLUENCE_PAGE_VIEW = 'confluence-page-view',
	CONFLUENCE_PAGE_EDIT = 'confluence-page-edit',
	CONFLUENCE_BLOG_VIEW = 'confluence-blog-view',
	CONFLUENCE_BLOG_EDIT = 'confluence-blog-edit',
	CONFLUENCE_WHITEBOARD = 'confluence-whiteboard',
	JIRA_HOME = 'jira-home',
	JIRA_FOR_YOU = 'jira-for-you',
	JIRA_ISSUE_VIEW = 'jira-issue-view',
	JIRA_PROJECT = 'jira-project',
	JIRA_CMP_SPACE_SETTINGS_PEOPLE_LEGACY = 'jira-cmp-space-settings-people-legacy',
	JIRA_CMP_SPACE_SETTINGS_PERMISSIONS_LEGACY = 'jira-cmp-space-settings-permissions-legacy',
	JIRA_TMP_SPACE_SETTINGS_ACCESS = 'jira-tmp-space-settings-access',
	JSM_INCIDENT_VIEW = 'jsm-incident-view',
	JSM_INCIDENT_LIST_VIEW = 'jsm-incident-list-view',
	JSM_ALERT_VIEW = 'jsm-alert-view',
	JSM_ALERT_LIST_VIEW = 'jsm-alert-list-view',
	JSM_ALERT_GROUP_DETAIL_VIEW = 'jsm-alert-group-detail-view',
	ATLAS_HOME = 'atlas-home',
	ATLAS_HOME_MAIN = 'atlas-home-main',
	ATLAS_HOME_DISCOVER = 'atlas-home-discover',
	ATLAS_HOME_RECENTS = 'atlas-home-recents',
	ATLAS_HOME_FULL_PAGE_NOTIFICATIONS = 'atlas-home-full-page-notifications',
	ATLAS_HOME_PROJECT_UPDATES_LIST = 'atlas-home-project-updates-list',
	ATLAS_HOME_GOAL_UPDATES_LIST = 'atlas-home-goal-updates-list',
	ATLAS_HOME_TEAM_UPDATES_LIST = 'atlas-home-team-updates-list',
	ATLAS_HOME_TAG_UPDATES_LIST = 'atlas-home-tag-updates-list',
	FOCUS = 'focus',
	FOCUS_FOR_YOU = 'focus-for-you',
	FOCUS_FOCUS_AREA_PAGE = 'focus-focus-area-page',
	FOCUS_FOCUS_AREA_GOAL_VIEW = 'focus-focus-area-goal-view',
	FOCUS_FOCUS_AREA_WORK_VIEW = 'focus-focus-area-work-view',
	SAC = 'sac',
	TRELLO_BOARD = 'trello-board',
	TRELLO_CARD = 'trello-card',
	UNKNOWN = 'unknown',
}

export type ConversationStarterLabelType =
	| 'be-productive'
	| 'stay-up-to-date'
	| 'summarize-content'
	| 'rephrase-content'
	| 'draft-content'
	| 'find-information'
	| 'find-people'
	| 'code-search'
	| 'measure-productivity'
	| 'get-answers'
	| 'check-status'
	| 'summarize-information'
	| 'analyze-content'
	| 'summarize-code'
	| 'manage-settings'
	| 'automation-rule';

export type FilterOption = {
	id: string;
	label: string;
};

export const PROMPT_OF_DAY_ACTION_ID = 'prompt-of-day';
